import React from 'react';
import { FormAlerts, Button, Field } from '@cordata/buffet';
import { Card, CardText, CardBody, CardFooter, Alert, Row, Col } from 'reactstrap';
import { Formik, Form } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Yup from 'yup';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import kebabcaseKeys from 'kebabcase-keys';

import { baseInstance as axios } from '~/utils';

const initialValues = {
  vendorCode: '',
  telephoneNumber: '',
  employeeId: '',
  ssn: '',
  commMethod: '',
};

/* eslint-disable consistent-return */
const validationSchema = Yup.object().shape({
  vendorCode: Yup.string().required(),
  telephoneNumber: Yup.string().required(),
  commMethod: Yup.string().required(),
  ssn: Yup.mixed().when('employeeId', (v) => {
    if (!v) return Yup.mixed().required();
  }),
  employeeId: Yup.mixed().when('ssn', (v) => {
    if (!v) return Yup.mixed().required();
  }),
}, [['ssn', 'employeeId']]);
/* eslint-enable consistent-return */

const preferredMethods = ['sms', 'email'].map(m => ({
  value: m,
  label: m,
}));

const B2WWelcomeForm = () => {
  const location = useLocation();

  const updatedValues = {
    ...initialValues,
    vendorCode: location.search && queryString.parse(location.search)['vendor-code'],
  };

  return (
    <Formik
      displayName="B2WWelcomeFormik"
      validateOnBlur={false}
      validateOnChange={false}

      initialValues={updatedValues}
      initalStatus={{ success: false }}

      validationSchema={validationSchema}

      onSubmit={(values, {
        setStatus, setErrors, setSubmitting,
      }) => {
        axios
          .post(
            'b2w/init',
            kebabcaseKeys(values, { deep: true }),
          )
          .then(() => {
            localStorage.removeItem('registration-code');
            localStorage.setItem('registration-info', JSON.stringify(values));

            setStatus({ success: true });

            window.location.reload();
          })
          .catch((e) => {
            const errs = e.response ? e.response.data.errors : e;

            setErrors({ server: e });

            if (
              errs &&
              Object.keys(errs).some(er =>
                Object.keys(values).includes(er))
            ) {
              Object.keys(errs).forEach(er =>
                setErrors({ [er]: errs[er] !== 'Required' ? errs[er] : 'is required' }));
            }
          })
          .then(() => setSubmitting(false));
      }}
    >
      {({
        values,
        errors,
        status,
        handleReset,
        submitCount,
        isSubmitting,
      }) => (
        <Card>
          <Form noValidate>
            <CardBody>
              {submitCount >= 1 && errors && (
                <FormAlerts errors={errors} />
              )}

              {status && status.success && (<Alert color="success">Form Sent!</Alert>)}
              {values.vendorCode === '' && (<Alert color="danger">Unable to complete form. Please get in contact with your manager.</Alert>)}

              <Field
                name="vendorCode"
                required
                type="hidden"
              />

              <Row form>
                <Col sm>
                  <Field
                    name="telephoneNumber"
                    type="tel"
                    required
                  />
                </Col>

                <Col sm>
                  <Field
                    name="commMethod"
                    type="radio-group"
                    options={preferredMethods}
                    label="Preferred Method of Communication"
                    inline
                    required
                    clearable={false}
                  />
                </Col>
              </Row>

              <hr />

              <CardText>Please fill in one of the following.</CardText>

              <Row form>
                <Col sm>
                  <Field
                    name="employeeId"
                    label="Employee ID"
                    placeholder="Enter Employee ID"
                  />
                </Col>

                <Col sm>
                  <Field
                    name="ssn"
                    label="Last 4 of SSN"
                    placeholder="Enter Last 4 of SSN"
                    step={1}
                    type="number"
                  />
                </Col>
              </Row>
            </CardBody>

            <CardFooter className="d-flex">
              <div className="mr-auto">
                <Button
                  color="danger"
                  type="button"
                  outline
                  disabled={values.vendorCode === '' || isSubmitting}
                  onClick={() =>
                    handleReset(updatedValues)}
                >
                  <FontAwesomeIcon icon="sync-alt" />
                  <span className="d-none d-md-inline ml-2">Reset</span>
                </Button>
              </div>

              <div className="ml-auto">
                <Button
                  type="submit"
                  disabled={values.vendorCode === '' || isSubmitting}
                >
                  {isSubmitting ? 'Saving ...' : 'Save'}
                </Button>
              </div>
            </CardFooter>
          </Form>
        </Card>
      )}
    </Formik>
  );
};

export default B2WWelcomeForm;
